import { useForm, useFieldArray, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import type { CurrencyCode } from '@kora/types';
import { formatMoney } from '../../lib/format.js';
import { PlatformModal } from './PlatformModal.js';

/**
 * Generate (draft) invoice modal (Phase D-2). Mirrors the server Zod in
 * apps/api/src/routes/platform/invoices.ts (POST /v1/platform/invoices):
 *   - currency is fixed to the subscription's billing currency (display-only;
 *     the server rejects a mismatch with 400 CURRENCY_MISMATCH);
 *   - one-or-more line items (description, quantity, unit, line total);
 *   - optional tax + notes; required audit reason.
 *
 * Money/quantity fields are `type="text" inputMode` (NOT type="number"): jsdom +
 * userEvent don't update number inputs, so the project standard is text inputs
 * with string Zod validation, converted to numbers on submit. We intentionally
 * do NOT force line total == quantity × unit (operator flexibility, e.g. a
 * manual discount line) — the running subtotal below is a convenience preview.
 */
const decimalString = (label: string) =>
  z
    .string()
    .trim()
    .regex(/^\d+(\.\d+)?$/, `${label} must be a non-negative number`);

const lineItemSchema = z.object({
  description: z.string().trim().min(1, 'Description is required').max(500),
  quantity: z
    .string()
    .trim()
    .regex(/^\d+$/, 'Quantity must be a whole number')
    .refine((s) => Number(s) >= 1, 'Quantity must be at least 1'),
  unitAmount: decimalString('Unit amount'),
  totalAmount: decimalString('Line total'),
});

const schema = z
  .object({
    dueDate: z.string().trim().min(1, 'Due date is required'), // 'YYYY-MM-DD'
    // ADR-0032 D2 — the period this invoice pays for ('YYYY-MM-DD'), both or neither.
    servicePeriodStart: z.string().trim().optional(),
    servicePeriodEnd: z.string().trim().optional(),
    lineItems: z.array(lineItemSchema).min(1, 'At least one line item is required'),
    taxAmount: z
      .string()
      .trim()
      .regex(/^\d+(\.\d+)?$/, 'Tax must be a non-negative number')
      .optional()
      .or(z.literal('')),
    notes: z.string().trim().max(2000).optional(),
    reason: z.string().trim().min(1, 'Reason is required').max(500, 'Reason must be 500 characters or fewer'),
  })
  .superRefine((v, ctx) => {
    const start = v.servicePeriodStart ?? '';
    const end = v.servicePeriodEnd ?? '';
    if ((start === '') !== (end === '')) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['servicePeriodEnd'], message: 'Give both the start and end of the period, or neither' });
    } else if (start !== '' && end <= start) {
      // 'YYYY-MM-DD' strings compare correctly as text.
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['servicePeriodEnd'], message: 'The period must end after it starts' });
    }
  });
type Values = z.infer<typeof schema>;

/**
 * 'YYYY-MM-DD' + n calendar months, clamped to the target month's last day
 * (Jan 31 + 1 month → Feb 28/29). Prefill only — the operator sees and can edit
 * the dates, and the server validates them; this decides nothing.
 */
export function addMonthsIsoDate(isoDate: string, months: number): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

const EMPTY_LINE = { description: '', quantity: '1', unitAmount: '', totalAmount: '' };

/**
 * Live subtotal/total preview. Isolated into its own component so its
 * per-keystroke re-render (via useWatch) does NOT re-render the parent's input
 * tree — a top-level watch() would, which makes userEvent.type drop characters
 * in jsdom (and is wasteful at runtime).
 */
function TotalsPreview({ control, currency }: { control: Control<Values>; currency: string }): JSX.Element {
  const lineItems = useWatch({ control, name: 'lineItems' });
  const taxAmount = useWatch({ control, name: 'taxAmount' });
  const subtotal = (lineItems ?? []).reduce((sum, li) => {
    const n = Number(li?.totalAmount);
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);
  const tax = Number(taxAmount);
  const total = subtotal + (Number.isFinite(tax) ? tax : 0);
  return (
    <div className="self-end text-right text-sm text-gray-700">
      <div>
        Subtotal: <span className="font-medium">{formatMoney(subtotal.toFixed(2), currency as CurrencyCode)}</span>
      </div>
      <div>
        Total: <span className="font-medium">{formatMoney(total.toFixed(2), currency as CurrencyCode)}</span>
      </div>
    </div>
  );
}

/**
 * Per-row auto-compute: keeps `totalAmount` = quantity × unitAmount as the
 * operator types, so they don't have to do the math by hand (D-2 follow-up).
 *
 * Renders nothing — isolated exactly like TotalsPreview above so its
 * per-keystroke useWatch re-render never re-renders the row's inputs (a parent
 * watch would, dropping characters under jsdom + userEvent; see the file
 * header). The operator may still hand-edit the total; a subsequent
 * quantity/unitAmount change overwrites that edit — the simpler semantic, we do
 * NOT remember a manual override (architect 2026-06-10). An empty or
 * non-numeric operand clears the total (not "NaN", not "0", not stale).
 * Precision mirrors TotalsPreview's toFixed(2).
 *
 * Fires only when quantity/unit CHANGE from the values the row was loaded with
 * (2026-09-13, draft editing): the modal can now open prefilled with a saved
 * draft, and recomputing on load would silently overwrite a deliberate manual
 * total (e.g. a discount line) the operator never touched. Comparing against the
 * previous values, rather than skipping the first run, is StrictMode-safe.
 */
function LineTotalAutoCompute({
  control,
  index,
  setValue,
}: {
  control: Control<Values>;
  index: number;
  setValue: UseFormSetValue<Values>;
}): null {
  const quantity = useWatch({ control, name: `lineItems.${index}.quantity` });
  const unitAmount = useWatch({ control, name: `lineItems.${index}.unitAmount` });
  const loaded = useRef({ quantity, unitAmount });
  useEffect(() => {
    if (loaded.current.quantity === quantity && loaded.current.unitAmount === unitAmount) return;
    loaded.current = { quantity, unitAmount };
    const q = parseFloat(quantity ?? '');
    const u = parseFloat(unitAmount ?? '');
    const bothPresent =
      (quantity ?? '').trim() !== '' &&
      (unitAmount ?? '').trim() !== '' &&
      Number.isFinite(q) &&
      Number.isFinite(u);
    setValue(`lineItems.${index}.totalAmount`, bothPresent ? (q * u).toFixed(2) : '', {
      shouldDirty: bothPresent,
      shouldValidate: false,
    });
  }, [quantity, unitAmount, index, setValue]);
  return null;
}

/** A saved draft to edit (PATCH /v1/platform/invoices/:id). Absent = create a new draft. */
export interface DraftInvoiceInitial {
  number: string;
  dueDate: string; // ISO datetime
  servicePeriodStart?: string | null; // ISO datetime
  servicePeriodEnd?: string | null;
  lineItems: { description: string; quantity: number; unitAmount: number; totalAmount: number }[];
  taxAmount: string; // Decimal string
  notes: string | null;
}

export function GenerateInvoiceModal({
  open,
  onClose,
  tenantName,
  currency,
  isSubmitting = false,
  errorMessage,
  onConfirm,
  initial,
  renewFrom,
}: {
  open: boolean;
  onClose: () => void;
  tenantName: string;
  /**
   * Edit mode (2026-09-13): prefill from a saved DRAFT. Mount the modal with a
   * `key` per invoice — the form's defaults are read once, at mount.
   */
  initial?: DraftInvoiceInitial;
  /**
   * The subscription's current period end (ISO) — enables the renewal preset, which
   * prefills the next period to start where the current one ends (ADR-0032 D10).
   */
  renewFrom?: string | null;
  /** The subscription's billing currency — fixed; shown read-only. */
  currency: string;
  isSubmitting?: boolean;
  errorMessage?: string;
  onConfirm: (body: {
    currency: string;
    dueDate: string;
    lineItems: { description: string; quantity: number; unitAmount: number; totalAmount: number }[];
    taxAmount?: number;
    notes?: string;
    servicePeriodStart?: string | null;
    servicePeriodEnd?: string | null;
    reason: string;
  }) => Promise<void> | void;
}): JSX.Element | null {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: initial
      ? {
          dueDate: initial.dueDate.slice(0, 10),
          servicePeriodStart: initial.servicePeriodStart ? initial.servicePeriodStart.slice(0, 10) : '',
          servicePeriodEnd: initial.servicePeriodEnd ? initial.servicePeriodEnd.slice(0, 10) : '',
          lineItems: initial.lineItems.map((li) => ({
            description: li.description,
            quantity: String(li.quantity),
            unitAmount: String(li.unitAmount),
            totalAmount: String(li.totalAmount),
          })),
          taxAmount: Number(initial.taxAmount) > 0 ? String(Number(initial.taxAmount)) : '',
          notes: initial.notes ?? '',
          reason: '',
        }
      : { dueDate: '', servicePeriodStart: '', servicePeriodEnd: '', lineItems: [{ ...EMPTY_LINE }], taxAmount: '', notes: '', reason: '' },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'lineItems' });

  if (!open) return null;

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const submit = (v: Values): void => {
    void onConfirm({
      currency,
      // Convert the date-only value to a full ISO datetime for the server.
      dueDate: new Date(v.dueDate).toISOString(),
      lineItems: v.lineItems.map((li) => ({
        description: li.description,
        quantity: Number(li.quantity),
        unitAmount: Number(li.unitAmount),
        totalAmount: Number(li.totalAmount),
      })),
      ...(v.taxAmount && v.taxAmount !== '' ? { taxAmount: Number(v.taxAmount) } : {}),
      // In edit mode an emptied note must reach the server to clear it.
      ...(v.notes ? { notes: v.notes } : initial?.notes ? { notes: '' } : {}),
      // ADR-0032 D2: a stated period is what makes paying this invoice a renewal.
      // Emptied in edit mode → send nulls so the server clears it.
      ...(v.servicePeriodStart && v.servicePeriodEnd
        ? {
            servicePeriodStart: new Date(v.servicePeriodStart).toISOString(),
            servicePeriodEnd: new Date(v.servicePeriodEnd).toISOString(),
          }
        : initial?.servicePeriodStart
          ? { servicePeriodStart: null, servicePeriodEnd: null }
          : {}),
      reason: v.reason,
    });
  };

  return (
    <PlatformModal
      open={open}
      onClose={handleClose}
      title={initial ? `Edit draft ${initial.number}` : 'Generate invoice'}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="generate-invoice-form"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md font-medium bg-indigo-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? (initial ? 'Saving…' : 'Generating…') : initial ? 'Save draft' : 'Generate draft'}
          </button>
        </>
      }
    >
      <form id="generate-invoice-form" onSubmit={handleSubmit(submit)}>
        {initial ? (
          <p className="text-sm text-gray-700 mb-3">
            Correct this draft for <span className="font-medium">{tenantName}</span>. It keeps its number and stays a{' '}
            <span className="font-medium">draft</span> — the customer sees nothing until you issue it.
          </p>
        ) : (
          <p className="text-sm text-gray-700 mb-3">
            Draft an invoice for <span className="font-medium">{tenantName}</span>. It is created in{' '}
            <span className="font-medium">draft</span> status — issue it separately to make it payable.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">Currency</span>
            <div
              className="w-full px-3 py-2 border rounded-md bg-gray-50 text-gray-600"
              aria-label="Currency (fixed to subscription billing currency)"
            >
              {currency}
            </div>
          </div>
          <div>
            <label htmlFor="invoice-due-date" className="block text-sm font-medium text-gray-700 mb-1">
              Due date
            </label>
            <input
              id="invoice-due-date"
              type="date"
              {...register('dueDate')}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
            />
            {errors.dueDate ? <p className="mt-1 text-sm text-red-600">{errors.dueDate.message}</p> : null}
          </div>
        </div>

        <fieldset className="mb-3 rounded-md border border-gray-200 p-3">
          <legend className="px-1 text-sm font-medium text-gray-700">Service period</legend>
          <p className="mb-2 text-xs text-gray-600">
            The period this invoice pays for. <span className="font-medium">A renewal must state it</span>: marking the invoice
            paid extends the subscription&apos;s coverage to the period&apos;s end. Leave both blank for a one-off charge (a setup
            fee or a credit), which does not change coverage.
          </p>
          {renewFrom ? (
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-600">Renew from current period end ({renewFrom.slice(0, 10)}):</span>
              {[1, 3, 12].map((n) => (
                <button
                  key={n}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    const start = renewFrom.slice(0, 10);
                    setValue('servicePeriodStart', start, { shouldValidate: true });
                    setValue('servicePeriodEnd', addMonthsIsoDate(start, n), { shouldValidate: true });
                    if (!getValues('dueDate')) setValue('dueDate', start, { shouldValidate: true });
                  }}
                  className="rounded border border-indigo-primary px-2 py-0.5 text-indigo-primary hover:bg-indigo-primary hover:text-white disabled:opacity-50"
                >
                  {n === 1 ? '1 month' : `${n} months`}
                </button>
              ))}
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="invoice-period-start" className="block text-xs font-medium text-gray-700 mb-1">
                Period start
              </label>
              <input
                id="invoice-period-start"
                type="date"
                {...register('servicePeriodStart')}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
              />
            </div>
            <div>
              <label htmlFor="invoice-period-end" className="block text-xs font-medium text-gray-700 mb-1">
                Period end
              </label>
              <input
                id="invoice-period-end"
                type="date"
                {...register('servicePeriodEnd')}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
              />
              {errors.servicePeriodEnd ? <p className="mt-1 text-sm text-red-600">{errors.servicePeriodEnd.message}</p> : null}
            </div>
          </div>
        </fieldset>

        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Line items</span>
          <button
            type="button"
            onClick={() => append({ ...EMPTY_LINE })}
            disabled={isSubmitting}
            className="text-sm text-indigo-primary hover:opacity-80 disabled:opacity-50"
          >
            + Add line
          </button>
        </div>

        <div className="space-y-2">
          {fields.map((field, i) => (
            <div key={field.id} className="grid grid-cols-12 gap-2 items-start">
              <LineTotalAutoCompute control={control} index={i} setValue={setValue} />
              <div className="col-span-5">
                <input
                  aria-label={`Line ${i + 1} description`}
                  type="text"
                  {...register(`lineItems.${i}.description` as const)}
                  disabled={isSubmitting}
                  placeholder="Description"
                  className="w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-primary"
                />
                {errors.lineItems?.[i]?.description ? (
                  <p className="mt-1 text-xs text-red-600">{errors.lineItems[i]?.description?.message}</p>
                ) : null}
              </div>
              <div className="col-span-2">
                <input
                  aria-label={`Line ${i + 1} quantity`}
                  type="text"
                  inputMode="numeric"
                  {...register(`lineItems.${i}.quantity` as const)}
                  disabled={isSubmitting}
                  placeholder="Qty"
                  className="w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-primary"
                />
                {errors.lineItems?.[i]?.quantity ? (
                  <p className="mt-1 text-xs text-red-600">{errors.lineItems[i]?.quantity?.message}</p>
                ) : null}
              </div>
              <div className="col-span-2">
                <input
                  aria-label={`Line ${i + 1} unit amount`}
                  type="text"
                  inputMode="decimal"
                  {...register(`lineItems.${i}.unitAmount` as const)}
                  disabled={isSubmitting}
                  placeholder="Unit"
                  className="w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-primary"
                />
                {errors.lineItems?.[i]?.unitAmount ? (
                  <p className="mt-1 text-xs text-red-600">{errors.lineItems[i]?.unitAmount?.message}</p>
                ) : null}
              </div>
              <div className="col-span-2">
                <input
                  aria-label={`Line ${i + 1} total`}
                  type="text"
                  inputMode="decimal"
                  {...register(`lineItems.${i}.totalAmount` as const)}
                  disabled={isSubmitting}
                  placeholder="Total"
                  className="w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-primary"
                />
                {errors.lineItems?.[i]?.totalAmount ? (
                  <p className="mt-1 text-xs text-red-600">{errors.lineItems[i]?.totalAmount?.message}</p>
                ) : null}
              </div>
              <div className="col-span-1 flex justify-end">
                {fields.length > 1 ? (
                  <button
                    type="button"
                    aria-label={`Remove line ${i + 1}`}
                    onClick={() => remove(i)}
                    disabled={isSubmitting}
                    className="text-gray-400 hover:text-red-600 disabled:opacity-50 px-1 py-1.5"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        {typeof errors.lineItems?.message === 'string' ? (
          <p className="mt-1 text-sm text-red-600">{errors.lineItems.message}</p>
        ) : null}

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label htmlFor="invoice-tax" className="block text-sm font-medium text-gray-700 mb-1">
              Tax (optional)
            </label>
            <input
              id="invoice-tax"
              type="text"
              inputMode="decimal"
              {...register('taxAmount')}
              disabled={isSubmitting}
              placeholder="0"
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
            />
            {errors.taxAmount ? <p className="mt-1 text-sm text-red-600">{errors.taxAmount.message}</p> : null}
          </div>
          <TotalsPreview control={control} currency={currency} />
        </div>

        <label htmlFor="invoice-notes" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Notes (optional)
        </label>
        <textarea
          id="invoice-notes"
          {...register('notes')}
          disabled={isSubmitting}
          rows={2}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Appears on the invoice PDF…"
        />
        {errors.notes ? <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p> : null}

        <label htmlFor="invoice-reason" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Reason
        </label>
        <textarea
          id="invoice-reason"
          {...register('reason')}
          disabled={isSubmitting}
          rows={2}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Written record for the audit trail…"
        />
        {errors.reason ? <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p> : null}
        {errorMessage ? (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>
    </PlatformModal>
  );
}
